import type { MetabaseResourceId } from './adminUserDashboard.queries'
import type { AdminUserMetabaseCollectionDashboard } from '@branditdev/mf-types'

const METABASE_SCRIPT_ID = 'metabase-guest-embed-script'
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000
const TOKEN_REFRESH_RETRY_MS = 30 * 1000

let metabaseEmbedScriptPromise: Promise<true> | null = null

export const EMPTY_DASHBOARDS: AdminUserMetabaseCollectionDashboard[] = []

export const getDashboardId = (dashboard?: AdminUserMetabaseCollectionDashboard | null): MetabaseResourceId | null =>
  dashboard?.id ?? dashboard?.dashboard_id ?? null

export const normalizeMetabaseSiteUrl = (siteUrl?: string | null) => {
  if (!siteUrl) return null

  return siteUrl.replace(/\/$/, '')
}

export const getTokenRefreshDelay = (expiresAt?: string | null) => {
  if (!expiresAt) return null

  const expiresAtMs = Date.parse(expiresAt)
  if (Number.isNaN(expiresAtMs)) return null

  return Math.max(expiresAtMs - Date.now() - TOKEN_REFRESH_BUFFER_MS, TOKEN_REFRESH_RETRY_MS)
}

export const loadMetabaseEmbedScript = (metabaseSiteUrl: string | null) => {
  const normalizedSiteUrl = normalizeMetabaseSiteUrl(metabaseSiteUrl)
  if (!normalizedSiteUrl) return Promise.reject(new Error('Missing Metabase site URL'))
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Metabase embeds require a browser environment'))
  }

  // Metabase reads this page-level config when its guest web component initializes.
  window.metabaseConfig = {
    ...(window.metabaseConfig || {}),
    isGuest: true,
    instanceUrl: normalizedSiteUrl
  }

  if (window.customElements?.get('metabase-dashboard')) {
    return Promise.resolve(true)
  }

  const existingScript = document.getElementById(METABASE_SCRIPT_ID) as HTMLScriptElement | null
  if (existingScript?.dataset.metabaseSiteUrl && existingScript.dataset.metabaseSiteUrl !== normalizedSiteUrl) {
    return Promise.reject(new Error('Metabase embed script already loaded for a different instance'))
  }

  if (existingScript?.dataset.loaded === 'true') {
    return Promise.resolve(true)
  }

  if (metabaseEmbedScriptPromise) {
    return metabaseEmbedScriptPromise
  }

  const script = existingScript ?? document.createElement('script')
  script.id = METABASE_SCRIPT_ID
  script.src = `${normalizedSiteUrl}/app/embed.js`
  script.defer = true
  script.dataset.metabaseSiteUrl = normalizedSiteUrl

  metabaseEmbedScriptPromise = new Promise<true>((resolve, reject) => {
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true'
        resolve(true)
      },
      { once: true }
    )
    script.addEventListener(
      'error',
      () => {
        metabaseEmbedScriptPromise = null
        reject(new Error('Failed to load Metabase embed script'))
      },
      { once: true }
    )
  })

  if (!existingScript) {
    document.head.appendChild(script)
  }

  return metabaseEmbedScriptPromise
}
